import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PageAction } from '../../components/page-actions/page-actions.component';
import { PackageService } from '@services/packages/package.service';
import { PackageInstalledAppsService } from '@services/packages/package-installed-apps.service';
import { CompositionService } from '@services/compositions/composition.service';
import { ImportService } from '@services/imports/import.service';
import { LegacyCompositionService } from '@services/legacy-compositions/legacy-composition.service';
import { CustomerService } from '@services/customers/customer.service';
import * as _ from 'lodash';
import { PackageInstalledWidgetsService } from '@services/packages/package-installed-widgets.service';
import { DynamicContentEntranceService } from '@services/dynamic-content/dynamic-content-entrance.service';
import { DynamicContentService } from '@services/dynamic-content/dynamic-content.service';
import { CompositionAppService } from '@services/compositions/composition-app.service';
import { ScreenSettingsService } from '@services/screen-settings/screen-settings.service';
import { CodeSnippetService } from '@services/code-snippets/code-snippet.service';
import { JsonService } from '@services/jsons/json.service';
import { PipeImportService } from '@services/pipe-imports/pipe-import.service';

@Component({
    selector: 'app-orphans',
    templateUrl: './orphans.component.html',
    styleUrls: ['./orphans.component.scss'],
    providers: [
        PackageService, PackageInstalledAppsService, PackageInstalledWidgetsService,
        DynamicContentEntranceService, DynamicContentService, CompositionAppService,
        CompositionService, ImportService, LegacyCompositionService, CustomerService,
        ScreenSettingsService, CodeSnippetService, JsonService, PipeImportService
    ],
    standalone: false
})
export class OrphansComponent implements OnInit {

  public title = 'Orphans';
  public pageActions: PageAction[] = [];

  public orphanData: any;

  public importerIds = [];
  public unassignedImporters = [];

  private limit = 100;
  public isCanceled = false;

  constructor(
    public packageService: PackageService, private packageInstalledAppsService: PackageInstalledAppsService, private pipeImportsService: PipeImportService,
    private widgetService: PackageInstalledWidgetsService, private compositionService: CompositionService, private compositionAppService: CompositionAppService,
    private dynamicContentEntranceService: DynamicContentEntranceService, private dynamicContentService: DynamicContentService, public importService: ImportService,
    private customerService: CustomerService, private legacyCompositionService: LegacyCompositionService, private screenSettingsService: ScreenSettingsService,
    private codeSnippetService: CodeSnippetService, private jsonService: JsonService
  ) {
    this.orphanData = {
      packageInstalledApps: {
        type: 'packageInstalledApps',
        description: 'Package-Installed-Apps without parent Package',
        service: this.packageInstalledAppsService,
        queryData: [
          {
            parentService: this.packageService,
            parentKey: 'packageId',
          }
        ],
        items: [],
        info: { running: false, checked: 0, total: null, done: false },
        privilege: { name: 'write', value: 'packages' }
      },
      packageInstalledWidgets: {
        type: 'packageInstalledWidgets',
        description: 'Package-Installed-Widgets without parent Package',
        service: this.widgetService,
        queryData: [
          {
            parentService: this.packageService,
            parentKey: 'packageId',
          }
        ],
        items: [],
        info: { running: false, checked: 0, total: null, done: false },
        privilege: { name: 'write', value: 'packages' }
      },
      dynamicContent: {
        type: 'dynamicContent',
        description: 'Dynamic Content without Dynamic Content Entrances',
        service: this.dynamicContentService,
        queryData: [
          {
            parentService: this.dynamicContentEntranceService,
            parentArrayKey: 'children'
          },
          {
            parentService: this.dynamicContentService,
            query: JSON.stringify({
              $or: [
                { children: { $in: ['var'] } },
                { translationEntries: { $in: ['var'] } }
              ]
            }),
            itemKey: '_id'
          }
        ],
        items: [],
        info: { running: false, checked: 0, total: null, done: false },
        privilege: { name: 'write', value: 'dynamic-contents' }
      },
      dynamicContentEntrance: {
        type: 'dynamicContentEntrance',
        description: 'Dynamic Content Entrances without Compositions',
        service: this.dynamicContentEntranceService,
        queryData: [
          {
            parentService: this.compositionService,
            parentArrayKey: 'dynamicContentIds',
          }
        ],
        items: [],
        info: { running: false, checked: 0, total: null, done: false },
        privilege: { name: 'write', value: 'dynamic-content-entrances' }
      },
      compositionApp: {
        type: 'compositionApp',
        description: 'Unassigned Composition Apps',
        service: this.compositionAppService,
        queryData: [
          {
            parentService: this.compositionService,
            parentKey: 'compositionId',
          }
        ],
        items: [],
        info: { running: false, checked: 0, total: null, done: false },
        privilege: { name: 'write', value: 'composition-apps' }
      },
      composition: {
        type: 'composition',
        description: 'Unassigned Compositions',
        service: this.compositionService,
        queryData: [
          {
            parentService: this.packageService,
            parentArrayKey: 'compositionIds'
          }
        ],
        items: [],
        info: { running: false, checked: 0, total: null, done: false },
        privilege: { name: 'write', value: 'compositions' }
      },
      legacyComposition: {
        type: 'legacyComposition',
        description: 'Unassigned legacy compositions',
        service: this.legacyCompositionService,
        queryData: [
          {
            parentService: this.customerService,
            query: JSON.stringify({ 'compositionNames': { $regex: 'var' } }),
            itemKey: 'name',
          }
        ],
        items: [],
        info: { running: false, checked: 0, total: null, done: false },
        privilege: { name: 'write', value: 'legacy-compositions' }
      },
      codeSnippets: {
        type: 'codeSnippets',
        description: 'Unassigned code snippets',
        service: this.codeSnippetService,
        queryData: [
          {
            parentService: this.screenSettingsService,
            query: JSON.stringify({
              $or: [
                { 'detail.codeSnippetNames': { $regex: 'var' } },
                { 'detail.launcherForceSnippet': { $regex: 'var' } }
              ]
            }),
            itemKey: 'name'
          },
          {
            parentService: this.jsonService,
            query: JSON.stringify({ data: { $regex: 'var' } }),
            itemKey: 'name'
          },
          {
            parentService: this.compositionService,
            parentArrayKey: 'snippetIds',
          },
          {
            parentService: this.pipeImportsService,
            query: { 'modules.data.snippetName': { $regex: 'var' } },
            itemKey: 'name'
          },
          {
            parentService: this.packageService,
            query: { 'ibexApp.customCss': { $regex: 'var' } },
            itemKey: 'name'
          }
        ],
        items: [],
        info: { running: false, checked: 0, total: null, done: false },
        privilege: { name: 'write', value: 'code-snippets' }
      },
      // importer: {
      //   type: 'importer',
      //   description: 'Unassigned Importer',
      //   service: this.importService,
      //   queryData: [
      //     {
      //       parentService: this.customerService,
      //       parentArrayKey: 'importIds',
      //     },
      //     {
      //       parentService: this.compositionService,
      //       parentArrayKey: 'importIds'
      //     },
      //     {
      //       parentService: this.legacyCompositionService,
      //       query: JSON.stringify({importIds : { $regex: 'var'}}),
      //       itemKey: '_id',
      //     }
      //     // {
      //     //   // todo meta Importer
      //     // }
      //   ],
      //   items: [],
      //   info: {running: false, checked: 0, total: null, done: false},
      //   privilege: {name: 'write', value: 'imports'}
      // }
    }
  }

  ngOnInit() {
  }

  public getOrphans(key) {
    this.isCanceled = false;
    const data = this.orphanData[key];
    this.getDeadItems(data.service, 0, data.queryData, key);
  }

  /* find dynamical by service */
  public getDeadItems(service, skip, queryData, key) {
    if (this.isCanceled) {
      this.orphanData[key].info.running = false;
      this.orphanData[key].info.done = true;
      return;
    }
    service.find(skip, this.limit, { $sort: { _id: -1 } }).then(items => {
      if (skip === 0) {
        this.orphanData[key].items = [];
        this.orphanData[key].info = { running: true, checked: 0, total: items.total, done: false }
      }
      items.data.map(item => {

        this.orphanData[key].info.checked++;

        const promises = queryData.map(queryItem => {

          if (queryItem.parentKey && (!item[queryItem.parentKey] || item[queryItem.parentKey] === 'no')) {
            return true; // packageInstalledApps and Widgets were unset with packageId: no
          } else {
            return this.findByQueryData(queryItem.parentService, queryItem, item).then(result => {

              return result;
            });
          }
        })

        Promise.all(promises).then(results => {
          if (!results.includes(false)) {
            this.orphanData[key].items.push(item);
          }
        });

      });

      if (skip + this.limit < items.total) {
        this.getDeadItems(service, skip + this.limit, queryData, key)
      } else {
        this.orphanData[key].info.running = false;
        this.orphanData[key].info.done = true;
      }

    });
  }

  public removeAll(service, items) {
    items.map(item => {
      if (this.isCanceled) {
        return;
      }
      this.removeOne(service, item._id, items);
    })
  }

  public removeOne(service, id, items) {
    if (this.isCanceled) {
      console.log('is canceled');
      return;
    }
    service.remove(id).then(() => {
      _.remove(items, { _id: id })
    });
  }

  public getUnassignedImporters(skip, max) {
    this.unassignedImporters = [];
    this.importerIds = [];
    this.getImporterIds(skip, max).then(ids => {
      console.log('collected all the ids: ', this.importerIds.length);
      this.importerIds.map(id => {
        this.customerService.find(0, this.limit, { importIds: id }).then(customer => {
          console.log('loop 1');
          if (customer && !customer.data.length) {
            this.compositionService.find(0, this.limit, { importIds: id }).then(composition => {
              console.log('loop 2');
              if (composition && !composition.data.length) {
                this.legacyCompositionService.find(0, this.limit, { importIds: { $regex: id } }).then(legacyComposition => {
                  console.log('loop 3');
                  if (legacyComposition && !legacyComposition.data.length) {
                    this.pushImporterDetail(id)
                  }
                });
              }
            });
          }
        });
      });
    });
  }

  public getImporterIds(skip, max): Promise<any> {
    const limit = 100;
    return this.importService.find(skip, this.limit, { typeId: { $ne: '5718d155f2feea56269bae9d' } }).then(result => {
      // $ne: typeId - skip content cache importer, they are probably not assigned
      result.data.map(importer => {
        this.importerIds.push(importer._id);
      });
      if (skip + limit < max) {
        return this.getImporterIds(skip + limit, max);
      } else {
        return Promise.resolve(true);
      }
    });
  }

  public pushImporterDetail(id) {
    this.importService.get(id).then(importer => {
      this.unassignedImporters.push(importer);
    })
  }

  public cancel() {
    this.isCanceled = true;
  }

  private find(serivce, query): Promise<boolean> {
    return serivce.find(0, 1, query).then(parent => {
      return parent && parent.data && !parent.data.length;
    });
  }

  private findByQueryData(parentService, queryData, item): Promise<boolean> {
    if (queryData.parentKey) {
      return this.find(parentService, { _id: item[queryData.parentKey] });
    } else if (queryData.parentArrayKey) {
      return this.find(parentService, { [queryData.parentArrayKey]: { $in: [item._id] } });
    } else if (queryData.query && queryData.itemKey) {
      const query = queryData.query.replace(/var/gm, item[queryData.itemKey]);
      return this.find(parentService, JSON.parse(query));
    }
  }
}
